import { createAsyncThunk } from '@reduxjs/toolkit';

import { backendCall, createApiCall } from '@reducers/utils';
import { sendLog } from '@reducers/dataDogs';
import { LOGGER_TITLES } from '@reducers/dataDogs/constants';
import { onClosedProductionOperation, onFailedToCloseProductionOperation } from '@reducers/v2/planboard/planboard.slice';
import { RootState, AppDispatch } from '@store/types';

export interface FinishProductionOperationPayload {
  productionOperationId: number;
  actualEndDate: string;
  actualQuantity: number;
  actualSetupTimeInSeconds: number;
  actualCycleTimeTotalInSeconds: number;
  noteDetails: string;
  machineId: number;
  // When the job runs on an active batch the BE accepts the finish but batch status keeps it Running
  // (Job-Status-Determination wiki), so skip the optimistic Finished flip — it would only revert.
  skipOptimistic?: boolean;
}

export const finishProductionOperation = createAsyncThunk<
  boolean,
  FinishProductionOperationPayload,
  { state: RootState; dispatch: AppDispatch }
>('planboard/finishProductionOperation', async (payload, { dispatch }) => {
  const { machineId, skipOptimistic, ...apiParams } = payload;

  const onCloseFailed = (errorMessage: string) => {
    dispatch(
      onFailedToCloseProductionOperation({
        productionOperationId: payload.productionOperationId,
        errorMessage,
      })
    );
  };

  // Optimistic: planboard removes the card, details reducer flips Status → Finished (search index lags).
  // Skipped for a job on an active batch — the flip would only revert on the next refresh.
  if (!skipOptimistic) {
    dispatch(
      onClosedProductionOperation({
        productionOperationId: payload.productionOperationId,
        optimisticFields: {
          actualEndDate: payload.actualEndDate,
          actualQuantity: payload.actualQuantity,
        },
      })
    );
  }

  try {
    const result = await dispatch(
      backendCall(
        createApiCall((api) => api.productionInformation.closeProductionOperation, {
          params: apiParams,
        })
      )
    );

    // backendCall can resolve with undefined on swallowed errors (reducers/utils.js catch block)
    if (!result || result.error) {
      const errorMessage = result?.error ?? 'Backend call returned empty result';

      dispatch(
        sendLog({
          title: LOGGER_TITLES.FINISH_PRODUCTION_OPERATION_FAILED,
          data: {
            error: errorMessage,
            productionOperationId: payload.productionOperationId,
            machineId,
          },
          status: 'error',
        })
      );

      onCloseFailed(errorMessage);

      return false;
    }

    return true;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    const errorStatus = (error as { status?: number })?.status;

    dispatch(
      sendLog({
        title: LOGGER_TITLES.FINISH_PRODUCTION_OPERATION_FAILED,
        data: {
          error: errorMessage,
          productionOperationId: payload.productionOperationId,
          machineId,
          status: errorStatus,
        },
        status: 'error',
      })
    );

    onCloseFailed(errorMessage);

    return false;
  }
});
